// Mode B's confirmation: what clearing a category on this project would
// actually remove, stated in full before anything happens.
//
// WHY THIS SCREEN IS NOT A GENERIC CONFIRM. Someone removed two categories
// believing it affected one project. It affected all twenty eight, because the
// category list is organisation wide and that was the only lever in the
// product. No harm followed only because those two lines happened to be empty
// everywhere else. This dialog exists so that the class of mistake cannot
// repeat: it names the project, every month, every amount, the split between
// typed and Xero money, and one total, before the button that acts is even
// reachable.
//
// THE REACH IS VERIFIED, NOT ASSERTED. The rows listed here are sent to
// pnl_clear_project_category as p_expected_row_ids, and the function re-reads
// the rows inside the same transaction and refuses with 40001 if the set moved
// since this dialog drew it. A dialog that states a reach it cannot guarantee
// is the failure this area keeps producing, so the reach statement and the act
// are checked against each other by the database rather than by this component
// hoping nothing changed.
//
// THE CANONICAL NAME, ALWAYS. A project may relabel a category on its own
// screen. That label is display only and never reaches a write. This dialog
// leads with the stored name because that is what is being deleted, and shows
// the project's own word for it beside, so nobody clears "Make good" believing
// "Patching" is safe.
//
// WHAT IT PROMISES ABOUT MONEY, AND WHAT IT DOES NOT.
//   * Typed values are archived and an admin can put them back. Said plainly,
//     with where from.
//   * Xero values are deleted and NOT archived, because the next sync writes
//     them back. Said plainly too, because being told a line was cleared and
//     then watching it return is how trust in a destructive control is lost.
//   * The company figures fall by the typed amount immediately, with no
//     refresh step, because pnl_fact is a plain view. Said plainly, because
//     that is the whole point of clearing rather than hiding.

import { useMemo, useState } from "react";
import { AlertTriangle, Archive, Building2, Check, Loader2, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { Modal } from "../ui/ToastModal";
import { formatCurrency, isSafeModeActive } from "../../lib/calculations";
import { formatMonthLabel } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { describeClearReach } from "../../features/pnlAnalytics/projectPnlView";
import type { ClearOutcome } from "../../features/pnlAnalytics/useClearProjectCategory";
import type { ProjectPnlValueRowWithId } from "./PnlProjectViewModal";
import "./PnlProjectView.css";

export interface PnlClearCategoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  /** The organisation's stored name. This is what is deleted and what is keyed on. */
  category: string;
  /** What this project calls it, when that differs. Shown so nobody clears the wrong line. */
  projectLabel: string;
  /** Every value row under this category on this project, both sources, every month. */
  rows: ProjectPnlValueRowWithId[];
  clearing: boolean;
  /** Receives exactly the row ids listed here, so the RPC can refuse if the set moved. */
  onConfirm: (expectedRowIds: string[]) => Promise<ClearOutcome>;
  /** Called when the RPC refused with 40001, so the caller can refetch before the user tries again. */
  onStale: () => void;
}

export function PnlClearCategoryDialog({
  isOpen,
  onClose,
  projectName,
  category,
  projectLabel,
  rows,
  clearing,
  onConfirm,
  onStale,
}: PnlClearCategoryDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const reach = useMemo(() => describeClearReach(category, rows), [category, rows]);
  const rowIds = useMemo(() => rows.map((row) => row.id), [rows]);

  // Safe Mode replaces every dollar figure with dots. A destructive confirm
  // whose amounts are masked is not a reach statement, it is a blank cheque,
  // so this refuses to act rather than quietly asking someone to approve a
  // number they cannot see. Same treatment the export button in
  // PnlDashboardCharts already gives itself.
  const masked = isSafeModeActive();

  if (!isOpen) return null;

  async function confirm() {
    setRefusal(null);
    const outcome = await onConfirm(rowIds);
    if (outcome.ok) return;
    setRefusal(outcome.message);
    setAcknowledged(false);
    if (outcome.stale) onStale();
  }

  const relabelled = projectLabel !== category;
  const ready = acknowledged && !clearing && !masked && rows.length > 0;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      hideFooter
      ariaLabel={`Clear the values under ${category} on ${projectName}`}
      title={
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)" }}>
            Clear {category} on this project
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 }}>
            {projectName}
            {relabelled ? `, where this line is shown as "${projectLabel}"` : ""}
          </div>
        </div>
      }
    >
      {refusal && (
        <div className="ppv-notice is-error" role="alert">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>{refusal}</span>
        </div>
      )}

      {masked && (
        <div className="ppv-notice is-warning">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>
            Safe Mode is on, so the amounts below are hidden. Nothing can be cleared while a figure on this screen
            cannot be read. Turn Safe Mode off to see what would be removed.
          </span>
        </div>
      )}

      <div className="ppv-reach-head">
        <p className="ppv-reach-title">
          This removes <span data-seq-private="" className="ppv-reach-total">{formatCurrency(reach.total, true)}</span> from{" "}
          {category} on {projectName}.
        </p>
        <p className="ppv-reach-sub">
          {reach.months.length} {reach.months.length === 1 ? "month" : "months"}, {reach.manualRowCount}{" "}
          typed {reach.manualRowCount === 1 ? "value" : "values"}
          {reach.includesXero
            ? ` and ${reach.xeroRowCount} Xero ${reach.xeroRowCount === 1 ? "value" : "values"}`
            : ""}
          . No other project is touched, and no other category on this one.
        </p>
      </div>

      <table className="ppv-months">
        <caption>Every month holding a value</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">Typed</th>
            {reach.includesXero && <th scope="col">Xero</th>}
            <th scope="col">Total</th>
          </tr>
        </thead>
        <tbody>
          {reach.months.map((month) => (
            <tr key={month.monthDate}>
              <td>{formatMonthLabel(month.monthDate)}</td>
              <td data-seq-private="" className={month.manual === 0 ? "ppv-zero" : undefined}>{formatCurrency(month.manual, true)}</td>
              {reach.includesXero && (
                <td data-seq-private="" className={month.xero === 0 ? "ppv-zero" : undefined}>{formatCurrency(month.xero, true)}</td>
              )}
              <td data-seq-private="">{formatCurrency(month.total, true)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td data-seq-private="">{formatCurrency(reach.manualTotal, true)}</td>
            {reach.includesXero && <td data-seq-private="">{formatCurrency(reach.xeroTotal, true)}</td>}
            <td data-seq-private="">{formatCurrency(reach.total, true)}</td>
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: 14 }}>
        <p className="ppv-consequence">
          <Building2 size={14} aria-hidden="true" />
          <span>
            <strong>This project's costs fall by this amount at company level too</strong>, immediately and with no
            refresh step, on the company dashboard and in P&amp;L Analytics. That is the point of clearing rather than
            hiding: the tab and the dashboard then agree because the data agrees.
          </span>
        </p>

        <p className="ppv-consequence">
          <Archive size={14} aria-hidden="true" />
          <span>
            <strong>
              The {reach.manualRowCount} typed {reach.manualRowCount === 1 ? "value is" : "values are"} kept, not
              destroyed.
            </strong>{" "}
            An organisation admin with financial access can put {reach.manualRowCount === 1 ? "it" : "them"} back from
            the undo on the confirmation, from this project's Rows editor, or from{" "}
            <strong>Cleared P&amp;L values</strong> in P&amp;L Analytics.
          </span>
        </p>

        {reach.includesXero && (
          <p className="ppv-consequence">
            <RefreshCw size={14} aria-hidden="true" />
            <span>
              <strong>
                The {reach.xeroRowCount} Xero {reach.xeroRowCount === 1 ? "value comes" : "values come"} back on the
                next sync
              </strong>{" "}
              for any month inside its window, because the sync writes them from Xero rather than from here. Change the
              account mapping in Xero to stop that. Xero values are not archived, for the same reason.
            </span>
          </p>
        )}

        <p className="ppv-consequence">
          <Trash2 size={14} aria-hidden="true" />
          <span>
            The row is then hidden on this project, in the same transaction. If a value is ever typed under{" "}
            {category} here again, the row comes straight back, because money the company counts always has a line on
            this tab.
          </span>
        </p>

        <p className="ppv-consequence">
          <ShieldAlert size={14} aria-hidden="true" />
          <span>
            The figures above are read again by the database before anything is deleted. If they have moved since this
            screen opened, nothing is cleared at all and you are shown the new ones.
          </span>
        </p>
      </div>

      <div className="ppv-confirm-field">
        <label className="ppv-confirm-check">
          <input
            type="checkbox"
            checked={acknowledged}
            disabled={clearing || masked}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>
            I am clearing <code>{category}</code> on <code>{projectName}</code>, and I have read the months above.
          </span>
        </label>
        {acknowledged && !clearing && (
          <span className="ppv-confirm-tick">
            <Check size={12} aria-hidden="true" /> Ready
          </span>
        )}
      </div>

      {/* The footer carries the two buttons and nothing else. At 390px a
          sentence in here pushed the acknowledgement off screen behind a three
          row footer, so the sentence moved up into the consequences where it
          is read in order with the rest. */}
      <div className="ppv-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={clearing}>
          Cancel
        </button>
        <button type="button" className="ppv-danger-btn" onClick={confirm} disabled={!ready}>
          {clearing ? <Loader2 size={14} className="spin" aria-hidden="true" /> : <Trash2 size={14} aria-hidden="true" />}
          {/* The exact amount, decimals and all. Rounding here while the
              statement above shows cents gives one act two different prices. */}
          {clearing ? "Clearing" : `Clear ${formatCurrency(reach.total, true)} from ${category}`}
        </button>
      </div>
    </Modal>
  );
}
